/**
 * src/lib/ThemeContext.jsx — Light/Dark/System Theme Provider
 * AC Nautitech Design System
 *
 * Persists user preference to localStorage.
 * Applies data-theme attribute to <html> for CSS variable switching.
 * Default for a new/unset user is 'light' (the explicit choice loads light even
 * on a dark-OS device); users who already picked a theme are read from storage.
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext({ theme: 'system', setTheme: () => {} });

const STORAGE_KEY = 'acn-theme';

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || 'light'; }
    catch { return 'light'; }
  });

  const applyTheme = useCallback((t) => {
    const root = document.documentElement;
    if (t === 'light' || t === 'dark') {
      root.setAttribute('data-theme', t);
    } else {
      // System: remove explicit attribute, let @media query handle it
      root.removeAttribute('data-theme');
    }
    // Update meta theme-color for mobile browser chrome.
    // Meta tags don't resolve CSS vars, so read the computed --bg value after
    // the data-theme attribute change takes effect.
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
      const bg = getComputedStyle(root).getPropertyValue('--bg').trim();
      if (bg) meta.setAttribute('content', bg);
    }
  }, []);

  const setTheme = useCallback((t) => {
    setThemeState(t);
    try { localStorage.setItem(STORAGE_KEY, t); } catch {}
    applyTheme(t);
  }, [applyTheme]);

  // Apply on mount + listen for OS preference changes
  useEffect(() => {
    applyTheme(theme);
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => { if (theme === 'system') applyTheme('system'); };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme, applyTheme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
